@if (currentSummary(); as s) {
  <section class="card p-4" aria-label="Provenance summary">
    <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <!-- LEFT: Paper stats + source chips + needs-attention (inline) -->
      <div class="flex flex-row flex-wrap items-center gap-x-2.5 gap-y-1.5">
        <span
          class="inline-flex items-baseline gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium text-slate-700"
        >
          <span class="text-2xl font-semibold">{{ s.totalPapers }}</span>
          @if (s.totalPapers === 1) {
            paper
          } @else {
            papers
          }
        </span>

        @for (chip of sourceChips(); track chip.source) {
          <span
            class="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700"
            [attr.data-source]="chip.source"
          >
            {{ chip.count }} {{ chip.label }}
          </span>
        }

        @if (s.itemsNeedingAttention > 0) {
          <button
            type="button"
            (click)="onNeedsAttentionClick()"
            class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-200 transition-colors"
            data-testid="needs-attention-chip"
            title="Papers that need manual review — click to filter"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
              class="h-3.5 w-3.5"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
            {{ s.itemsNeedingAttention }} need attention
          </button>
        }
      </div>

      <!-- RIGHT: Readiness checks + advance button (inline) -->
      @if (showAdvanceButton) {
        @let chk = readiness();
        <div class="flex flex-row flex-wrap items-center gap-x-2.5 gap-y-1.5">
          @if (s.totalPapers === 0) {
            <span class="text-xs text-slate-500">No papers imported yet.</span>
          } @else if (_suppressNeedsAttention()) {
            <span class="text-xs text-amber-700">Import in progress…</span>
          } @else {
            <!-- Status pill -->
            <span
              class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap cursor-default"
              [class.bg-red-100]="chk.status === 'blocked'"
              [class.text-red-700]="chk.status === 'blocked'"
              [class.bg-amber-100]="chk.status === 'warning'"
              [class.text-amber-700]="chk.status === 'warning'"
              [class.bg-green-100]="chk.status === 'ready'"
              [class.text-green-700]="chk.status === 'ready'"
              (mouseenter)="showPopover($event, 'status')"
              (mouseleave)="hidePopover()"
            >
              @if (chk.status === 'blocked') {
                Blocked
              } @else if (chk.status === 'warning') {
                Warning
              } @else {
                Ready
              }
            </span>

            <!-- Papers pill -->
            <span
              class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap cursor-default"
              [class.bg-green-50.text-green-700]="chk.papersComplete"
              [class.bg-red-50.text-red-700]="!chk.papersComplete"
              (mouseenter)="showPopover($event, 'papers')"
              (mouseleave)="hidePopover()"
            >
              <span class="font-semibold">Papers</span>
              <span>
                @if (chk.papersComplete) {
                  ✓
                } @else {
                  ✗ {{ chk.needsAttention }}
                }
              </span>
            </span>

            <!-- Criteria pill -->
            <span
              class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap cursor-default"
              [class.bg-green-50.text-green-700]="chk.criteriaDefined"
              [class.bg-red-50.text-red-700]="!chk.criteriaDefined"
              (mouseenter)="showPopover($event, 'criteria')"
              (mouseleave)="hidePopover()"
            >
              <span class="font-semibold">Criteria</span
              ><span>{{ chk.criteriaDefined ? ' ✓' : ' ✗' }}</span>
            </span>

            <!-- Protocol pill -->
            <span
              class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap cursor-default"
              [class.bg-green-50.text-green-700]="chk.protocolFilled"
              [class.bg-red-50.text-red-700]="!chk.protocolFilled"
              (mouseenter)="showPopover($event, 'protocol')"
              (mouseleave)="hidePopover()"
            >
              <span class="font-semibold">Protocol</span
              ><span>{{ chk.protocolFilled ? ' ✓' : ' ✗' }}</span>
            </span>
          }

          <!-- Button -->
          <button
            type="button"
            (click)="onAdvanceClick()"
            [disabled]="!advanceEnabled"
            class="whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 text-xs font-semibold rounded-lg px-3 py-1.5"
            [class.btn-primary]="chk.status !== 'blocked'"
            [class.btn-secondary]="chk.status === 'blocked'"
            data-testid="advance-screening-button"
          >
            @if (chk.status === 'blocked' || s.totalPapers === 0) {
              Blocked
            } @else if (chk.status === 'warning') {
              Force Advance
            } @else {
              Advance
            }
          </button>
        </div>
      }

      <!-- Fallback completion note (non-PI view) -->
      @if (!showAdvanceButton && completionNote(); as note) {
        <p
          class="text-xs"
          [class.text-green-700]="readiness().allReady"
          [class.text-amber-700]="!readiness().allReady"
          data-testid="completion-note"
        >
          {{ note }}
        </p>
      }
    </div>
  </section>

  <!-- Hover popover (outside card to avoid stacking-context issues) -->
  @if (popover(); as p) {
    <div
      class="fixed -translate-x-1/2 z-[9999] bg-white rounded-xl py-3 px-4 max-w-[280px] w-max border border-slate-200 shadow-lg animate-[popover-in_0.12s_cubic-bezier(0.4,0,0.2,1)]"
      [style.left.px]="p.x"
      [style.top.px]="p.y"
    >
      <p class="text-xs font-semibold text-slate-900 mb-1">{{ p.title }}</p>
      <p class="text-xs text-slate-600 leading-relaxed">{{ p.body }}</p>
    </div>
  }
}
